import React, { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconLock, IconLockOpen, IconPlus } from '@tabler/icons-react';
import { useGetPrescriptions } from '../../../global/entities/Prescription/queries/useGetPrescriptions.tsx';
import {
  IPrescription,
  Prescription,
  PrescriptionLockedStatus,
  PrescriptionUnlockReason,
} from '../../../global/entities/Prescription/Prescription.ts';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import EditPrescriptionTableRow from './EditPrescriptionTableRow.tsx';
import { usePutPrescription } from '../../../global/entities/Prescription/mutations/usePutPrescriptionMutations.tsx';
import { useValidatePrescriptions } from '../../../global/entities/Prescription/mutations/useValidatePrescriptions.tsx';
import { useUnlockPrescriptions } from '../../../global/entities/Prescription/mutations/useUnlockPrescriptions.tsx';
import { ValidateConfirmModal } from '../Widget/PrescriptionHistory/ValidateConfirmModal.tsx';
import { UnlockModal } from '../Widget/PrescriptionHistory/UnlockModal.tsx';
import { UnauthorizedUnlockModal } from '../Widget/PrescriptionHistory/UnauthorizedUnlockModal.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';

export type IEditPrescriptionTableProps = {
  routeStore?: RouteStore;
};

const EditPrescriptionTable: React.FC<IEditPrescriptionTableProps> = inject(
  'routeStore',
)(
  observer(({ routeStore }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id: patientIdFromRoute } = useParams<{ id: string }>();
    const { currentUser } = useSessionProvider();
    const patientId = patientIdFromRoute ?? currentUser?.patientID;
    const isHCP = !!currentUser?.healthcareProfessionalID;

    const {
      data: prescriptionData,
      refetch: refetchPrescriptionData,
      isLoading,
    } = useGetPrescriptions();
    const {
      sortedData,
      setSortedData,
      setErrors,
      sortBy,
      reverseSortDirection,
      setSorting,
    } = useTable();
    const putPrescription = usePutPrescription();
    const validateMutation = useValidatePrescriptions(patientId);
    const unlockMutation = useUnlockPrescriptions(patientId);

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [validateTargetIds, setValidateTargetIds] = useState<number[] | null>(
      null,
    );
    const [unlockTargetIds, setUnlockTargetIds] = useState<number[] | null>(
      null,
    );
    const [showUnauthorized, setShowUnauthorized] = useState(false);

    useEffect(() => {
      setSortedData([]);
      if (prescriptionData?.prescriptions?.length)
        setSortedData(prescriptionData?.prescriptions);
    }, [prescriptionData?.prescriptions, setSortedData]);

    useEffect(() => {
      setSelectedIds((prev) => {
        const validIds = new Set((sortedData ?? []).map((p) => p.id));
        const next = new Set<number>();
        prev.forEach((id) => {
          if (validIds.has(id)) next.add(id);
        });
        return next;
      });
    }, [sortedData]);

    const addNewMedication = () => {
      setSortedData((prev) => {
        const usedNegativeIds =
          prev?.map((item) => item.id).filter((id) => id < 0) ?? [];

        let newId = -1;
        while (usedNegativeIds.includes(newId)) newId--;

        const newMedication: IPrescription = {
          id: newId,
          drugName: '',
          doseFrequencyID: 0,
          unitID: 0,
          dose: 0,
          startDate: undefined,
          endDate: undefined,
          lockedStatus: PrescriptionLockedStatus.Draft,
        };

        return [...(prev ?? []), newMedication];
      });
    };

    const validateFieldsRow = (rowsToValidate: IPrescription[]) => {
      let isValid = true;
      const newErrors: PrescriptionRowError[] = rowsToValidate
        .map((prescription) => {
          const errors: Partial<
            Record<
              | 'drugNameError'
              | 'doseError'
              | 'unitIDError'
              | 'doseFrequencyIDError'
              | 'startDateError'
              | 'endDateError',
              string
            >
          > = {};
          Object.entries({
            drugNameError: !prescription.drugName,
            doseError: !prescription.dose,
            unitIDError: !prescription.unitID,
            doseFrequencyIDError: !prescription.doseFrequencyID,
            startDateError:
              prescription.startDate === undefined ||
              (prescription.endDate !== undefined &&
                prescription.startDate > prescription.endDate),
            endDateError:
              prescription.endDate !== undefined &&
              prescription.startDate !== undefined &&
              prescription.startDate > prescription.endDate,
          }).forEach(([key, condition]) => {
            if (condition) {
              switch (key) {
                case 'drugNameError':
                  errors.drugNameError = t('formError.drugNameError');
                  break;
                case 'doseError':
                  errors.doseError = t('formError.doseIsMissing');
                  break;
                case 'unitIDError':
                  errors.unitIDError = t('formError.unitIsMissing');
                  break;
                case 'doseFrequencyIDError':
                  errors.doseFrequencyIDError = t(
                    'formError.doseFrequencyIsMissing',
                  );
                  break;
                case 'startDateError':
                  if (prescription.startDate === undefined)
                    errors.startDateError = t('formError.dateIsRequired');
                  else
                    errors.startDateError = t('formError.dateMustBeInferior');

                  break;
                case 'endDateError':
                  if (prescription.endDate === undefined)
                    errors.endDateError = t('formError.dateIsRequired');
                  else errors.endDateError = t('formError.dateMustBeSuperior');

                  break;
              }
              isValid = false;
            }
          });

          return Object.keys(errors).length
            ? { id: prescription.id ?? 0, ...errors }
            : null;
        })
        .filter(
          (
            error,
          ): error is {
            id: number;
          } => Boolean(error),
        );
      setErrors(newErrors);
      return isValid;
    };

    const persistDrafts = async (): Promise<boolean> => {
      const draftRows = sortedData.filter(
        (p: IPrescription) =>
          p.lockedStatus !== PrescriptionLockedStatus.Locked,
      );
      if (draftRows.length === 0) return true;
      if (!validateFieldsRow(draftRows)) return false;
      const formattedPrescriptions = draftRows.map(({ ...rest }) => ({
        ...rest,
        id: rest.id > 0 ? rest.id : null,
        startDate: rest.startDate
          ? new Date(rest.startDate).toISOString()
          : undefined,
        endDate: rest.endDate
          ? new Date(rest.endDate).toISOString()
          : undefined,
      }));
      try {
        await putPrescription.mutateAsync(formattedPrescriptions as any);
        await refetchPrescriptionData();
        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    };

    const handleSave = async () => {
      const ok = await persistDrafts();
      if (ok) navigate(-1);
    };

    const drafts = useMemo(
      () =>
        (sortedData ?? []).filter(
          (p) => p.lockedStatus !== PrescriptionLockedStatus.Locked && p.id > 0,
        ),
      [sortedData],
    );
    const selectedDrafts = useMemo(
      () => drafts.filter((p) => selectedIds.has(p.id)) as Prescription[],
      [drafts, selectedIds],
    );
    const selectedLocked = useMemo(
      () =>
        (sortedData ?? []).filter(
          (p) =>
            p.lockedStatus === PrescriptionLockedStatus.Locked &&
            selectedIds.has(p.id),
        ) as Prescription[],
      [sortedData, selectedIds],
    );

    const unitsLabel = (unitID: number): string | undefined => {
      return prescriptionData?.unitsCombobox.find(
        (u) => Number(u.value) === unitID,
      )?.label;
    };

    const handleSelectRow = (id: number, selected: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selected) next.add(id);
        else next.delete(id);
        return next;
      });
    };

    const openValidateForIds = (ids: number[]) => {
      if (ids.length === 0) return;
      setValidateTargetIds(ids);
    };

    const openUnlockForIds = (ids: number[]) => {
      if (ids.length === 0) return;
      setUnlockTargetIds(ids);
    };

    const numericPatientId = patientId ? Number(patientId) : undefined;

    const confirmValidate = async () => {
      if (!numericPatientId || !validateTargetIds) return;
      const saved = await persistDrafts();
      if (!saved) return;
      try {
        await validateMutation.mutateAsync({
          patientID: numericPatientId,
          prescriptionIDs: validateTargetIds,
        });
        setValidateTargetIds(null);
        setSelectedIds(new Set());
        await refetchPrescriptionData();
      } catch (e) {
        setValidateTargetIds(null);
        console.error(e);
      }
    };

    const confirmValidateAll = async () => {
      if (!numericPatientId) return;
      const saved = await persistDrafts();
      if (!saved) return;
      try {
        await validateMutation.mutateAsync({
          patientID: numericPatientId,
          all: true,
        });
        setValidateTargetIds(null);
        setSelectedIds(new Set());
        await refetchPrescriptionData();
      } catch (e) {
        setValidateTargetIds(null);
        console.error(e);
      }
    };

    const confirmUnlock = async (
      reasonCode: PrescriptionUnlockReason,
      note: string | null,
    ) => {
      if (!numericPatientId || !unlockTargetIds) return;
      try {
        await unlockMutation.mutateAsync({
          patientID: numericPatientId,
          prescriptionIDs: unlockTargetIds,
          reasonCode,
          note,
        });
        setUnlockTargetIds(null);
        setSelectedIds(new Set());
        await refetchPrescriptionData();
      } catch (error: any) {
        setUnlockTargetIds(null);
        if (error?.httpStatusCode === 403) setShowUnauthorized(true);
        console.error(error);
      }
    };

    const allRowsSelectable = (sortedData ?? []).filter((p) => p.id > 0);
    const allSelected =
      allRowsSelectable.length > 0 &&
      allRowsSelectable.every((p) => selectedIds.has(p.id));
    const someSelected = selectedIds.size > 0 && !allSelected;

    const validatingHcpName = currentUser?.displayName ?? '';

    const prescriptionsForValidate = useMemo(() => {
      if (!validateTargetIds) return [];
      return drafts.filter((p) =>
        validateTargetIds.includes(p.id),
      ) as Prescription[];
    }, [drafts, validateTargetIds]);

    const prescriptionsForUnlock = useMemo(() => {
      if (!unlockTargetIds) return [];
      return (sortedData ?? []).filter((p) =>
        unlockTargetIds.includes(p.id),
      ) as Prescription[];
    }, [sortedData, unlockTargetIds]);

    return (
      <Paper
        p="md"
        withBorder
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <Stack gap="lg" style={{ flex: 1, overflow: 'auto' }}>
          <Group justify="space-between" align="center">
            <Title order={4}>
              {t('table.prescriptionHistory.prescriptionHistory')}
            </Title>
            {isHCP && (
              <Button
                variant="filled"
                color="green"
                leftSection={<IconLock size={14} />}
                onClick={() => {
                  const draftIds = drafts.map((p) => p.id);
                  if (draftIds.length === 0) return;
                  setValidateTargetIds(draftIds);
                }}
                disabled={drafts.length === 0}
              >
                {t('prescriptionHistory.actions.validateAll')}
              </Button>
            )}
          </Group>

          {isHCP && selectedIds.size > 0 && (
            <Paper
              p="sm"
              withBorder
              radius="md"
              bg="var(--mantine-color-gray-0)"
            >
              <Group justify="space-between">
                <Text size="sm">
                  {t('prescriptionHistory.edit.selectedCount', {
                    count: selectedIds.size,
                  })}
                </Text>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="outline"
                    color="green"
                    leftSection={<IconLock size={12} />}
                    onClick={() =>
                      openValidateForIds(selectedDrafts.map((p) => p.id))
                    }
                    disabled={selectedDrafts.length === 0}
                  >
                    {t('prescriptionHistory.actions.validateSelected', {
                      count: selectedDrafts.length,
                    })}
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    color="orange"
                    leftSection={<IconLockOpen size={12} />}
                    onClick={() =>
                      openUnlockForIds(selectedLocked.map((p) => p.id))
                    }
                    disabled={selectedLocked.length === 0}
                  >
                    {t('prescriptionHistory.actions.unlockSelected', {
                      count: selectedLocked.length,
                    })}
                  </Button>
                </Group>
              </Group>
            </Paper>
          )}

          <Box>
            <ScrollArea w="100%">
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 40 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => {
                          if (e.currentTarget.checked)
                            {setSelectedIds(
                              new Set(allRowsSelectable.map((p) => p.id)),
                            );}
                          else setSelectedIds(new Set());
                        }}
                        aria-label={t('prescriptionHistory.edit.selectAllAria')}
                      />
                    </Table.Th>
                    <Th
                      sorted={sortBy === 'drugName'}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting('drugName', sortedData)}
                    >
                      {t('table.prescriptionHistory.medication')}
                    </Th>
                    <Th
                      sorted={sortBy === 'dose'}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting('dose', sortedData)}
                    >
                      {t('table.prescriptionHistory.dose')}
                    </Th>
                    <Th
                      sorted={sortBy === 'unitID'}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting('unitID', sortedData)}
                    >
                      {t('table.prescriptionHistory.unit')}
                    </Th>
                    <Th
                      sorted={sortBy === 'doseFrequencyID'}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting('doseFrequencyID', sortedData)}
                    >
                      {t('table.prescriptionHistory.frequency')}
                    </Th>
                    <Th
                      width={130}
                      sorted={sortBy === 'startDate'}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting('startDate', sortedData)}
                    >
                      <Stack align={'self-start'} gap={0}>
                        {t('table.prescriptionHistory.startDate')}
                        <Text size={'xs'} c={'dimmed'}>
                          {t('placeHolder.dateFormat')}
                        </Text>
                      </Stack>
                    </Th>
                    <Th
                      width={130}
                      sorted={sortBy === 'endDate'}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting('endDate', sortedData)}
                    >
                      <Stack align={'self-start'} gap={0}>
                        {t('table.prescriptionHistory.endDate')}
                        <Text size={'xs'} c={'dimmed'}>
                          {t('placeHolder.dateFormat')}
                        </Text>
                      </Stack>
                    </Th>
                    <Table.Th>
                      {t('prescriptionHistory.badge.columnHeader')}
                    </Table.Th>
                    <Table.Th>
                      {t('prescriptionHistory.edit.actionsHeader')}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedData?.map((prescription: IPrescription) => {
                    return (
                      <EditPrescriptionTableRow
                        key={prescription.id}
                        prescription={prescription}
                        index={prescription.id}
                        selected={selectedIds.has(prescription.id)}
                        onSelectChange={handleSelectRow}
                        onValidate={(id) => openValidateForIds([id])}
                        onUnlock={(id) => openUnlockForIds([id])}
                      />
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            <Stack
              mt="md"
              gap="sm"
              style={{ width: 'fit-content', marginLeft: 'auto' }}
            >
              <Button
                fullWidth
                loading={isLoading}
                variant="filled"
                leftSection={<IconPlus size={16} />}
                onClick={() => addNewMedication()}
              >
                {t('table.dosageSimulation.addMedication')}
              </Button>

              <Group align={'flex-end'} w="100%" gap="sm" wrap="nowrap">
                <Button
                  variant="outline"
                  loading={isLoading}
                  onClick={() => navigate(routeStore?.lastRoute || '..')}
                >
                  {t('button.cancel')}
                </Button>
                <Button loading={isLoading} onClick={handleSave}>
                  {t('button.save')}
                </Button>
              </Group>
            </Stack>
          </Box>

          <Divider />
        </Stack>

        <ValidateConfirmModal
          opened={validateTargetIds !== null}
          onClose={() => setValidateTargetIds(null)}
          onConfirm={
            validateTargetIds &&
            validateTargetIds.length === drafts.length &&
            drafts.length > 0
              ? confirmValidateAll
              : confirmValidate
          }
          prescriptions={prescriptionsForValidate}
          validatingHcpName={validatingHcpName}
          isLoading={validateMutation.isPending}
          unitsLabel={unitsLabel}
        />
        <UnlockModal
          opened={unlockTargetIds !== null}
          onClose={() => setUnlockTargetIds(null)}
          onConfirm={confirmUnlock}
          prescriptions={prescriptionsForUnlock}
          unlockingHcpName={validatingHcpName}
          isLoading={unlockMutation.isPending}
        />
        <UnauthorizedUnlockModal
          opened={showUnauthorized}
          onClose={() => setShowUnauthorized(false)}
        />
      </Paper>
    );
  }),
);

const WrappedEditPrescriptionTable: React.FC = () => {
  return (
    <TableContextProvider>
      <EditPrescriptionTable />
    </TableContextProvider>
  );
};

export default WrappedEditPrescriptionTable;

type PrescriptionRowError = {
  id?: number;
  drugNameError?: string;
  doseError?: string;
  unitIDError?: string;
  doseFrequencyIDError?: string;
  startDateError?: string;
  endDateError?: string;
};
